// src/components/property/ImagePlaceholderSkeleton.tsx
/**
 * ImagePlaceholderSkeleton Component
 *
 * Skeleton placeholder shown when no processed images exist for the selected aspect ratio.
 * Clicking shows a popover explaining the user needs to process images.
 */

import { useState } from 'react';
import { ImageIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { IImagePlaceholderSkeletonProps } from '@/@types/property';

function ImagePlaceholderSkeleton({ aspectRatio, index }: IImagePlaceholderSkeletonProps) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  // Dynamic aspect ratio class based on selected ratio
  const aspectClass = aspectRatio === '9:16' ? 'aspect-[9/16]' : 'aspect-video';

  return (
    <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
      <PopoverTrigger asChild>
        <div
          className={cn(
            'relative rounded-lg overflow-hidden cursor-pointer',
            'bg-muted animate-pulse',
            'border-2 border-dashed border-muted-foreground/20',
            'hover:border-muted-foreground/40 transition-colors',
            aspectClass
          )}
          aria-label={`Image placeholder ${index + 1}`}
        >
          <div className="absolute inset-0 flex items-center justify-center">
            <ImageIcon className="h-8 w-8 text-muted-foreground/30" />
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-64 text-center">
        <p className="text-sm text-muted-foreground">Process images to view in this aspect ratio</p>
      </PopoverContent>
    </Popover>
  );
}

export { ImagePlaceholderSkeleton };
